import React, { useContext } from 'react';
import {Text} from 'react-native';
import {RouteProp} from '@react-navigation/core';
import PlansContext from '../../../contexts/PlansContext';
import {PlanStackParamList} from '../PlanRoutes';
import {Header, Title} from '../../../styled-components/Header';
import WebView from '../../general/WebView';
import {ScrollView} from '../../general/ScrollView';
import {Contents} from '../../../database/schemas/content';
import Audio from '../../general/Audio';
import {plansDir} from '../../../hooks/usePlans';
import { contentUrl, isWeb } from '../../../api';
import { useContentSignature } from '../../ContentSignature';
import ExpandableContent from './ExpandableContent';
import ListContent from './ListContent';
import PDFContent from './PDFContent';
import CenteredIndicator from '../../general/CenteredIndicator';
import Centered from '../../../styled-components/Centered';
import {withContentRevision} from '../../../utils/contentAssetUrl';

type PlanRouteProp = RouteProp<PlanStackParamList, 'Content'>;

interface ContentProps {
  route: PlanRouteProp;
}

const Content = ({route}: ContentProps) => {
  const {planId, contentId} = route.params;
  const context = useContext(PlansContext);
  const headerColors = Contents.headerColors(context!.doa, contentId);
  const content = context!.doa.getContent(planId, contentId);
  const {
    contentSignature,
    contentSignatureError,
    contentSignatureLoading,
  } = useContentSignature();

  const contentAssetUrl = (url: string) =>
    isWeb && content ? withContentRevision(url, content.lastUpdate) : url;

  if (!content) {
    return (
      <Header background={headerColors.background}>
        <Title color={headerColors.tint}>Not Found</Title>
      </Header>
    );
  } else {
    return (
      <>
        <Header background={headerColors.background}>
          <Title color={headerColors.tint}>{content.title}</Title>
        </Header>
        {content.type === 'L' && (
          <ListContent planId={planId} contentId={content.contentID} />
        )}
        {content.type === 'E' && (
          <ExpandableContent planId={planId} contentId={content.contentID} />
        )}
        {content.type === 'H' && (
          contentSignatureLoading ? (
            <CenteredIndicator />
          ) : (
            <ScrollView>
              <WebView
                remoteFetchDisabled={
                  !!contentSignatureError || !contentSignature
                }
                remoteFetchError={contentSignatureError}
                source={{
                  uri: contentAssetUrl(`${contentUrl}/${planId}/${
                    content.contentID
                  }/${content.indexFile!}${contentSignature}`),
                }}
              />
            </ScrollView>
          )
        )}
        {content.type === 'F' && (
          <PDFContent
            content={content}
            planId={planId}
            contentSignature={contentSignature}
            contentSignatureError={contentSignatureError}
            contentSignatureLoading={contentSignatureLoading}
          />
        )}
        {content.type === 'A' && (
          contentSignatureLoading ? (
            <CenteredIndicator />
          ) : contentSignatureError ? (
            <Centered>
              <Text>{contentSignatureError}</Text>
            </Centered>
          ) : (
            <Audio
              key={`${content.contentID}-${content.lastUpdate.valueOf()}`}
              source={{
                id: content.file!,
                title: content.title,
                artist: 'Artist',
                url: contentAssetUrl(`${contentUrl}/${planId}/${
                  content.contentID
                }/${content.file!}${contentSignature}`),
              }}
            />
          )
        )}
      </>
    );
  }
};

export default Content;
